/**
 * BUILD OPERATIONAL LINEAGE GRAPH
 *
 * Construct a deterministic lineage graph from an OperationalBundleV1.
 *
 * INVARIANTS:
 * - No Math.random
 * - All node IDs derived from content hashes
 * - proofOnly-safe metadata only
 * - No backend dependency
 */

import type { OperationalBundleV1 } from '../../server/export/operationalBundleTypes.js';
import type {
  OperationalLineageGraph,
  LineageNode,
  LineageEdge,
} from './operationalLineageTypes.js';
import { computeLineageNodeId, computeLineageEdgeId, computeLineageGraphHash } from './operationalLineageHash.js';

export async function buildOperationalLineageGraph(
  bundle: OperationalBundleV1
): Promise<OperationalLineageGraph> {
  const nodes: LineageNode[] = [];
  const edges: LineageEdge[] = [];

  // --- Event Stream Node ---
  const eventStreamId = computeLineageNodeId('event_stream', bundle.eventStreamHash);
  nodes.push({
    id: eventStreamId,
    type: 'event_stream',
    label: `Event Stream: ${bundle.workspaceId}`,
    hash: bundle.eventStreamHash,
    metadata: {
      workspaceId: bundle.workspaceId,
      projectionWindow: bundle.projectionWindow,
    },
    createdAt: bundle.createdAt,
  });

  // --- Projection Node ---
  const projectionId = computeLineageNodeId('projection', bundle.operationalWorkspaceHash);
  nodes.push({
    id: projectionId,
    type: 'projection',
    label: `Projection: ${bundle.operationalWorkspaceHash.slice(0, 20)}...`,
    hash: bundle.operationalWorkspaceHash,
    metadata: {
      workspaceId: bundle.workspaceId,
      projectionWindow: bundle.projectionWindow,
      proofOnly: bundle.proofOnly,
    },
    createdAt: bundle.createdAt,
  });

  // event_stream → projection
  edges.push({
    id: computeLineageEdgeId(eventStreamId, projectionId, 'produced'),
    source: eventStreamId,
    target: projectionId,
    type: 'produced',
    metadata: {},
  });

  // --- Pane Model Nodes ---
  for (const paneHash of bundle.paneModelHashes) {
    const paneId = computeLineageNodeId('pane_model', paneHash.paneHash);
    nodes.push({
      id: paneId,
      type: 'pane_model',
      label: `Pane: ${paneHash.paneId}`,
      hash: paneHash.paneHash,
      metadata: {
        paneId: paneHash.paneId,
        rendered: bundle.renderedPaneTypes.includes(paneHash.paneId),
      },
      createdAt: bundle.createdAt,
    });

    // projection → pane_model
    edges.push({
      id: computeLineageEdgeId(projectionId, paneId, 'rendered_as'),
      source: projectionId,
      target: paneId,
      type: 'rendered_as',
      metadata: { paneId: paneHash.paneId },
    });

    // pane_model → projection (hash_bound_to)
    edges.push({
      id: computeLineageEdgeId(paneId, projectionId, 'hash_bound_to'),
      source: paneId,
      target: projectionId,
      type: 'hash_bound_to',
      metadata: {},
    });
  }

  // --- Continuity State Node ---
  const continuityHash = `sha256:${bundle.continuityState}:${bundle.continuitySummary.totalTransitions}`;
  const continuityId = computeLineageNodeId('continuity_state', continuityHash);
  nodes.push({
    id: continuityId,
    type: 'continuity_state',
    label: `Continuity: ${bundle.continuityState}`,
    hash: continuityHash,
    metadata: {
      currentStatus: bundle.continuitySummary.currentStatus,
      totalTransitions: bundle.continuitySummary.totalTransitions,
      refusalCount: bundle.continuitySummary.refusalCount,
      boundaryViolationCount: bundle.continuitySummary.boundaryViolationCount,
    },
    createdAt: bundle.createdAt,
  });

  // projection → continuity_state
  edges.push({
    id: computeLineageEdgeId(projectionId, continuityId, 'continuity_transition'),
    source: projectionId,
    target: continuityId,
    type: 'continuity_transition',
    metadata: {},
  });

  // --- Replay Window Nodes ---
  if (bundle.replayWindows && bundle.replayWindows.length > 0) {
    for (const rw of bundle.replayWindows) {
      const replayId = computeLineageNodeId('replay_window', rw.windowId);
      nodes.push({
        id: replayId,
        type: 'replay_window',
        label: `Replay: ${rw.windowId}`,
        hash: null,
        metadata: {
          windowId: rw.windowId,
          start: rw.start,
          end: rw.end,
          eventCount: rw.eventCount,
        },
        createdAt: bundle.createdAt,
      });

      // event_stream → replay_window
      edges.push({
        id: computeLineageEdgeId(eventStreamId, replayId, 'replayed_as'),
        source: eventStreamId,
        target: replayId,
        type: 'replayed_as',
        metadata: {},
      });
    }
  }

  // --- Bundle Node ---
  const bundleId = computeLineageNodeId('bundle', bundle.bundleHash);
  nodes.push({
    id: bundleId,
    type: 'bundle',
    label: `Bundle: ${bundle.bundleHash.slice(0, 20)}...`,
    hash: bundle.bundleHash,
    metadata: {
      bundleVersion: bundle.bundleVersion,
      workspaceId: bundle.workspaceId,
      disclosureProfile: bundle.disclosureProfile,
      proofOnly: bundle.proofOnly,
    },
    createdAt: bundle.createdAt,
  });

  // projection → bundle
  edges.push({
    id: computeLineageEdgeId(projectionId, bundleId, 'derived_from'),
    source: projectionId,
    target: bundleId,
    type: 'derived_from',
    metadata: {},
  });

  // --- Attestation Node (if present) ---
  if (bundle.signature && bundle.signedBundleHash) {
    const attestationId = computeLineageNodeId('attestation', bundle.signedBundleHash);
    nodes.push({
      id: attestationId,
      type: 'attestation',
      label: `Attestation: ${bundle.signerType || 'unknown'}`,
      hash: bundle.signedBundleHash,
      metadata: {
        signerType: bundle.signerType,
        signingAlgorithm: bundle.signingAlgorithm,
        attestationScope: bundle.attestationScope,
        replayIntegrityGuarantee: bundle.replayIntegrityGuarantee,
        attestedAt: bundle.attestedAt,
      },
      createdAt: bundle.attestedAt ?? bundle.createdAt,
    });

    // bundle → attestation
    edges.push({
      id: computeLineageEdgeId(bundleId, attestationId, 'signed_by'),
      source: bundleId,
      target: attestationId,
      type: 'signed_by',
      metadata: {},
    });

    // attestation → bundle (hash_bound_to)
    edges.push({
      id: computeLineageEdgeId(attestationId, bundleId, 'hash_bound_to'),
      source: attestationId,
      target: bundleId,
      type: 'hash_bound_to',
      metadata: {},
    });
  }

  // --- Verifier Result Node ---
  const verifierHash = `verifier:${bundle.bundleHash}`;
  const verifierId = computeLineageNodeId('verifier_result', verifierHash);
  nodes.push({
    id: verifierId,
    type: 'verifier_result',
    label: 'Verifier Result',
    hash: null,
    metadata: {
      proofOnly: bundle.proofOnly,
      disclosureProfile: bundle.disclosureProfile,
      replayAvailability: bundle.replayAvailability,
      continuityState: bundle.continuityState,
    },
    createdAt: bundle.createdAt,
  });

  // bundle → verifier_result
  edges.push({
    id: computeLineageEdgeId(bundleId, verifierId, 'verified_by'),
    source: bundleId,
    target: verifierId,
    type: 'verified_by',
    metadata: {},
  });

  // Build graph and compute hash
  const graph: Omit<OperationalLineageGraph, 'graphHash'> = {
    graphVersion: 'operational-lineage-graph/v1',
    bundleHash: bundle.bundleHash,
    createdAt: bundle.createdAt,
    nodes,
    edges,
    nodeCount: nodes.length,
    edgeCount: edges.length,
  };

  const graphHash = await computeLineageGraphHash(graph);

  return {
    ...graph,
    graphHash,
  };
}
